import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { normalizeError } from "../lib/normalizeError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Truck, Package, CheckCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function Receiving() {
  const { getAuthHeader, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiveDialog, setReceiveDialog] = useState(null);
  const [qtyReceived, setQtyReceived] = useState(0);
  const [overrideNote, setOverrideNote] = useState("");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/receiving`, { headers: getAuthHeader() });
      setOrders(res.data);
    } catch (error) {
      toast.error("Failed to load receiving queue");
    } finally {
      setLoading(false);
    }
  };

  const handleReceive = async () => {
    if (!receiveDialog) return;
    
    const expectedQty = receiveDialog.final_qty || receiveDialog.suggested_qty;
    const needsOverride = qtyReceived !== expectedQty;
    
    if (needsOverride && !overrideNote.trim()) {
      toast.error("Override note is required when quantity doesn't match");
      return;
    }
    
    try {
      await axios.post(`${API}/receiving/${receiveDialog.id}`, {
        qty_received: qtyReceived,
        override_note: needsOverride ? overrideNote : null
      }, { headers: getAuthHeader() });
      
      toast.success("Order received successfully");
      setReceiveDialog(null);
      setQtyReceived(0);
      setOverrideNote("");
      fetchOrders();
    } catch (error) {
      toast.error(normalizeError(error.response?.data) || "Failed to receive order");
    }
  };

  const openReceiveDialog = (order) => {
    setReceiveDialog(order);
    setQtyReceived(order.final_qty || order.suggested_qty);
    setOverrideNote("");
  };

  const canReceive = ["admin", "receiving"].includes(user?.role);

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Truck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">RECEIVING</h1>
            <p className="text-xs text-muted-foreground">Process incoming inventory</p>
          </div>
        </div>
        
        <Badge variant="outline" className="font-mono">
          {orders.length} pending
        </Badge>
      </div>
      
      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No orders waiting for receiving</p>
            </div>
          ) : (
            <div className="industrial-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="industrial-table-header">
                    <TableHead>SKU</TableHead>
                    <TableHead>ITEM</TableHead>
                    <TableHead>SUPPLIER</TableHead>
                    <TableHead className="text-right">EXPECTED QTY</TableHead>
                    <TableHead>ORDERED DATE</TableHead>
                    <TableHead className="w-32">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} className="industrial-table-row" data-testid={`receiving-row-${order.id}`}>
                      <TableCell className="font-mono text-sm">{order.item_sku}</TableCell>
                      <TableCell className="font-medium">{order.item_name}</TableCell>
                      <TableCell className="text-muted-foreground">{order.supplier_name || "-"}</TableCell>
                      <TableCell className="text-right font-mono text-primary">
                        {order.final_qty || order.suggested_qty}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {order.ordered_at ? new Date(order.ordered_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>
                        {canReceive && (
                          <Button
                            size="sm"
                            onClick={() => openReceiveDialog(order)}
                            className="h-8 uppercase text-xs tracking-wider rounded-sm"
                            data-testid={`receive-btn-${order.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Receive
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </ScrollArea>
      
      {/* Receive Dialog */}
      <Dialog open={!!receiveDialog} onOpenChange={(open) => !open && setReceiveDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">
              RECEIVE INVENTORY
            </DialogTitle>
            <DialogDescription>
              Enter the quantity received. If different from expected, provide a note.
            </DialogDescription>
          </DialogHeader>
          
          {receiveDialog && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/30 rounded-sm space-y-2">
                <p className="text-sm text-muted-foreground">Item</p>
                <p className="font-medium">{receiveDialog.item_name}</p>
                <p className="font-mono text-sm text-muted-foreground">{receiveDialog.item_sku}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Expected Qty</p>
                  <p className="font-mono text-xl text-primary">
                    {receiveDialog.final_qty || receiveDialog.suggested_qty}
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Qty Received</label>
                  <Input
                    type="number"
                    value={qtyReceived}
                    onChange={(e) => setQtyReceived(parseFloat(e.target.value) || 0)}
                    className="industrial-input font-mono text-lg"
                    data-testid="receive-qty-input"
                  />
                </div>
              </div>
              
              {qtyReceived !== (receiveDialog.final_qty || receiveDialog.suggested_qty) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-400">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs uppercase">Quantity mismatch - override note required</span>
                  </div>
                  <Textarea
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    placeholder="Explain the discrepancy..."
                    className="industrial-input min-h-[80px]"
                    data-testid="override-note-input"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReceiveDialog(null)} className="uppercase tracking-wider rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleReceive} className="uppercase tracking-wider rounded-sm" data-testid="confirm-receive-btn">
              Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
